import React, {useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Popover, Drawer, Menu, message, notification, Divider, Typography, Tabs } from 'antd';
import {
    updateUserPortfolioData,
    deletePortfolios,
    updatePortfolioDetails,
    fetchDetailedPortfolioObjects
} from "../../../../api/portfolioApi";
import {BorderOutlined, CheckSquareOutlined, EditOutlined} from "@ant-design/icons";
import PortfolioForm from "../../PortfolioForm/PortfolioForm";
import PortfolioPageForm from "../../PortfolioPages/PortfolioPageForm";
import TableSearch from "../../../../components/TableSearch";
import {PlusOutlined, DeleteOutlined} from "@ant-design/icons";
import {selectPortfolioById} from "../../../../store/slices/portfolios.slice";
import PortfoliosToolbar from "./PortfoliosToolbar";
import configureTableColumns from "./tableConfig";
import {useScreenWidth} from "../../../../hooks/useScreen";
import NavTrail from "../../../../layouts/NavTrail";
import _ from 'lodash';
import {QueueRounded} from "@mui/icons-material";


export const sortByInUse = (portfolioList) => {
  portfolioList.sort((a, b) => {
    const aStarred = _.get(a, '_user.in_use', false) ? 1 : 0;
    const bStarred = _.get(b, '_user.in_use', false) ? 1 : 0;
    return bStarred - aStarred || a?.name.localeCompare(b?.name);
  });
};


const SelectedPortfoliosMenu = ({ selectedPortfolios, handleRemove, setNewPageDrawerVisible }) => {
  const handleMenuClick = (e) => {
    if (e.key === 'add') {
      setNewPageDrawerVisible(true);
    } else if (e.key === 'delete') {
      handleRemove();
    }
  };

  const menu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="add" icon={<PlusOutlined />}>
        Add to New Page
      </Menu.Item>
      <Menu.Item key="delete" icon={<DeleteOutlined />}>
        Delete
      </Menu.Item>
    </Menu>
  );

  return (
    <Popover
      content={menu}
      trigger="hover"
      placement="bottomLeft"
    >
      <CheckSquareOutlined />
    </Popover>
  );
};

const PortfoliosTable = ({ portfolios, prefix }) => {
  const dispatch = useDispatch();
  const [view, setView] = useState('table');
  const [editingPortfolioId, setEditingPortfolioId] = useState(null);
  const [filteredPortfolios, setFilteredPortfolios] = useState(portfolios); // New state for filtered portfolios
  const [originalPortfolios, setOriginalPortfolios] = useState(portfolios);
  const [selectedPortfolios, setSelectedPortfolios] = useState([]); // New state for selected portfolio
  const [newPageDrawerVisible, setNewPageDrawerVisible] = useState(false);
  const portfolioToEdit = useSelector((state) => selectPortfolioById(state, editingPortfolioId));
  const { width } = useScreenWidth();

  const handleUpdatePortfolioDetails = useCallback((payload) => {
    dispatch(updatePortfolioDetails({ data: payload.data, portfolioId: payload.portfolioId }))
        .then((response) => {
            console.log(response)
            // Assuming response contains the updated portfolio details
            if (response?.meta?.requestStatus === 'fulfilled') {
                setFilteredPortfolios(prevPortfolios =>
                    prevPortfolios.map(p => p.id === payload.portfolioId ? { ...p, ...payload.data } : p)
                );
                setOriginalPortfolios(prevPortfolios =>
                    prevPortfolios.map(p => p.id === payload.portfolioId ? { ...p, ...payload.data } : p)
                );
                message.success('Portfolio updated successfully!');
            } else {
                message.error('Failed to update the portfolio.');
            }
        })
        .catch(error => {
            message.error('Error updating portfolio.');
        });
}, [dispatch]);



  useEffect(() => {setOriginalPortfolios(portfolios)}, [portfolios]);

  const finalFilteredPortfolios = useMemo(() => {
    return filteredPortfolios.length ? filteredPortfolios : [];
  }, [filteredPortfolios]);

  sortByInUse(finalFilteredPortfolios);

  // Optimistic Update for toggling favorite
  const toggleFavorite = (portfolioID, isStarred) => {
      setFilteredPortfolios(prevPortfolios =>
        prevPortfolios.map(portfolio => portfolio.id === portfolioID ? { ...portfolio, _user: { ...portfolio._user, in_use: isStarred } } : portfolio)
      );
      setOriginalPortfolios(prevPortfolios =>
        prevPortfolios.map(portfolio => portfolio.id === portfolioID ? { ...portfolio, _user: { ...portfolio._user, in_use: isStarred } } : portfolio)
      );
      dispatch(updateUserPortfolioData({portfolioID, updateFields: { in_use: isStarred }})).catch(error => {
        setFilteredPortfolios(prevPortfolios =>
          prevPortfolios.map(portfolio => portfolio.id === portfolioID ? { ...portfolio, _user: { ...portfolio._user, in_use: !isStarred } } : portfolio)
        );
        setOriginalPortfolios(prevPortfolios =>
          prevPortfolios.map(portfolio => portfolio.id === portfolioID ? { ...portfolio, _user: { ...portfolio._user, in_use: !isStarred } } : portfolio)
        );
      });
    };


const handleFilter = useCallback(
  ({ searchTerm, filters }) => {
    let filtered = originalPortfolios;
    console.log(filters);

    // Search by name
    if (searchTerm) {
      filtered = filtered.filter((portfolio) =>
        portfolio.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Check if any of the filters are active
    const { block, sleeve, model } = filters;
    if (block || sleeve || model) {
      filtered = filtered.filter((portfolio) => {
        return (
          (block && portfolio.type === 'b') ||
          (sleeve && portfolio.type === 's') ||
          (model && portfolio.type === 'm')
        );
      });
    }

    console.log("Filtered", filtered);

    setFilteredPortfolios(filtered);
  },
  [originalPortfolios]
);


  const handleEdit = useCallback((portfolioId) => {
      console.log('Edit Portfolio', portfolioId)
      if (!portfolioId) {
          return;
      }
      dispatch(fetchDetailedPortfolioObjects([portfolioId])).then((resp)=>{
          console.log(resp)
          setEditingPortfolioId(portfolioId);
          setView('edit');
      })
  });

  const handleRemove = () => {
    if (selectedPortfolios.length > 0) {
      dispatch(deletePortfolios({ portfolioIds: selectedPortfolios }))
        .then(() => {
            notification.success({
                message: 'Portfolios Removed',
                description: 'Portfolios successfully removed '
            })
        })
        .catch((error) => {
          message.error(`An error occurred while removing portfolios. ${error}`);
        });
    }
    else {
        message.error("No portfolios selected to remove")
    }
  };

  const checkboxTitle = selectedPortfolios.length > 0 ? <SelectedPortfoliosMenu {...{selectedPortfolios, handleRemove, setNewPageDrawerVisible}}/>:<BorderOutlined size={16}/>
  let columns = configureTableColumns({
      handleEdit,
      toggleFavorite,
      prefix,
  });

  const onSelectedRowKeysChange = (selectedRowKeys) => {
      setSelectedPortfolios(selectedRowKeys);
  }
  const trailItems = [
      {label: "Portfolios", path: '/portfolios', onClick: ()=>setView('table')},
  ];
  if (view==='edit' && portfolioToEdit){
      trailItems.push({label: <Typography.Text><Typography.Text style={{color: 'var(--red)', fontWeight: 400}}>Edit </Typography.Text>{portfolioToEdit.name}</Typography.Text>, icon: <EditOutlined/>})
  }

  return (
      <>
          <NavTrail items={trailItems}/>
          {view === 'table' && (
              <div style={{padding: '24px 48px'}}>

                  <PortfoliosToolbar onFilter={handleFilter} portfolios={originalPortfolios} />
                  <TableSearch
                       dataSource={finalFilteredPortfolios}
                       columns={columns}
                       className={''}
                       rowKey={'id'}
                       pagination={{ pageSize: 100 }}
                       style={{fontSize: '11px'}}
                       scroll={{ y: 800, x: 'max-content'}}
                       rowSelection={{
                           selectedRowKeys: selectedPortfolios,
                           onChange: onSelectedRowKeysChange,
                           columnTitle: checkboxTitle
                       }}
                       tableLayout={'auto'}
                    />
              </div>
          )}
          {view === 'edit' && (
              <>
                  {editingPortfolioId && portfolioToEdit && (
                      <PortfolioForm
                        portfolioId={editingPortfolioId}
                        initialValues={portfolioToEdit}
                        handleSubmit={handleUpdatePortfolioDetails}
                        onClose={()=>setView('table')}

                        // Pass any additional props needed for the PortfolioForm
                      />
                    )}
              </>
          )}
            <Drawer
                title="Add New Portfolio Page"
                width={900}
                onClose={() => setNewPageDrawerVisible(false)}
                visible={newPageDrawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
            >
                <PortfolioPageForm
                    initialValues={{ portfolio_ids: [...selectedPortfolios] }} // Pass selected portfolios as initial values
                    onSubmit={(values)=>{console.log(values)}} // You need to define this function to handle form submission
                />
            </Drawer>
      </>
  )
};

export default PortfoliosTable;
